/**
 * InputPane — text input area with controls for the pipeline editor.
 *
 * Presentational component: receives all data and callbacks via props.
 * Compact header with type/mode badges, semantic type selector, and visualizer dropdown.
 */

import { FileUp, Maximize2, ShieldCheck, ShieldOff } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SearchBar } from '@/components/common/SearchBar';
import { TypeBadge } from '@/components/common/TypeBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRawTextSearch } from '@/hooks/useRawTextSearch';
import { useUndoableTextarea } from '@/hooks/useUndoableTextarea';
import { cn, formatBytes } from '@/lib/utils';
import type { VisualizerMode } from '@/store/useVisualizerStore';

import { QuickSwitchBar } from './QuickSwitchBar';

export interface InputPaneProps {
  value: string;
  onChange: (value: string) => void;
  onPaste: () => void;
  onClear: () => void;

  onDragOver: React.DragEventHandler<HTMLTextAreaElement>;
  onDrop: React.DragEventHandler<HTMLTextAreaElement>;
  inputMimeType: string | null;
  semanticTypeOptions: ReadonlyArray<{ value: string; label: string }>;
  selectedSemanticTypeValue: string;
  onSemanticTypeChange: (value: string) => void;
  /** Pass null to hide the stats bar. */
  stats: { bytes: number; chars: number; lines: number } | null;
  /** Optional visualizer overlay — replaces the textarea when provided. */
  visualizerOverlay?: React.ReactNode;
  /** Current auto-detected semantic type for display */
  detectedTypeName?: string;
  /** Current effective visualizer mode name */
  effectiveModeName?: string;

  /** Whether the semantic type was auto-detected (true) or manually selected (false) */
  isAutoDetectedType?: boolean;
  /** Whether a visualizer step can be added (false when one already exists) */
  canAddVisualizer?: boolean;
  /** Whether a pinned (explicit) visualizer step is active */
  isExplicitVisualizerActive?: boolean;
  /** Callback to add a visualizer step for a given mode */
  onAddVisualizerStep?: (mode: VisualizerMode) => void;
  /** Callback to remove (unpin) the visualizer step */
  onRemoveVisualizerStep?: () => void;
  /** Whether the active visualizer supports editing */
  isVisualizerEditable?: boolean;
  /** Whether this panel is in fullscreen mode */
  isFullscreen?: boolean;
  /** Toggle fullscreen */
  onToggleFullscreen?: () => void;
  /** Quick-switch bar: activated modes for this panel */
  quickSwitchActiveModes?: VisualizerMode[];
  /** Quick-switch bar: currently active mode */
  quickSwitchCurrentMode?: string;
  /** Quick-switch bar: switch to a mode */
  onQuickSwitchMode?: (mode: VisualizerMode) => void;
  /** Quick-switch bar: remove a mode from the bar */
  onQuickSwitchRemove?: (mode: VisualizerMode) => void;
  /** Quick-switch bar: all available modes for the "+" button */
  quickSwitchAvailableModes?: VisualizerMode[];
  /** Quick-switch bar: add a mode via the "+" button */
  onQuickSwitchAdd?: (mode: VisualizerMode) => void;
  /** Whether input persistence is enabled for this tab */
  persistInput?: boolean;
  /** Toggle input persistence */
  onTogglePersistInput?: () => void;
  /** Callback to import a file as pipeline input */
  onImportFile?: () => void;
}

export const InputPane: React.FC<InputPaneProps> = ({
  value,
  onChange,
  onDragOver,
  onDrop,
  stats,
  visualizerOverlay,
  detectedTypeName,
  effectiveModeName: _effectiveModeName,
  isAutoDetectedType = true,
  semanticTypeOptions,
  selectedSemanticTypeValue,
  onSemanticTypeChange,
  isVisualizerEditable,
  isFullscreen,
  onToggleFullscreen,
  quickSwitchActiveModes,
  quickSwitchCurrentMode,
  onQuickSwitchMode,
  onQuickSwitchRemove,
  quickSwitchAvailableModes,
  onQuickSwitchAdd,
  persistInput,
  onTogglePersistInput,
  onImportFile,
}) => {
  // FR-SEARCH-1: In-panel search for raw text view
  const { t } = useTranslation('editor');
  const search = useRawTextSearch(value);

  // Preserve browser undo stack by keeping the textarea uncontrolled
  const undoable = useUndoableTextarea({ value, onChange });

  return (
  <div ref={search.containerRef} className="flex-1 min-w-0 min-h-0 flex flex-col border-r border-border overflow-hidden">
    {/* Compact panel header */}
    <div className="h-6 flex items-center gap-1.5 px-2.5 border-b border-border bg-background shrink-0">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground shrink-0">
        {t('input.title')}
      </span>

      {/* Type badge */}
      {detectedTypeName && (
        <TypeBadge type={detectedTypeName} isAuto={isAutoDetectedType} />
      )}

      {/* Quick-switch tab bar (replaces old mode selector dropdown) */}
      {quickSwitchActiveModes && quickSwitchCurrentMode && onQuickSwitchMode && onQuickSwitchRemove && (
        <QuickSwitchBar
          activeModes={quickSwitchActiveModes}
          currentMode={quickSwitchCurrentMode}
          onSwitchMode={onQuickSwitchMode}
          onRemoveMode={onQuickSwitchRemove}
          availableModes={quickSwitchAvailableModes}
          onAddMode={onQuickSwitchAdd}
        />
      )}

      {/* Editable badge */}
      {isVisualizerEditable && visualizerOverlay && (
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shrink-0">
          {t('input.editable')}
        </span>
      )}

      {/* Semantic type select */}
      <div className="w-28 ml-1 shrink-0">
        <Select value={selectedSemanticTypeValue} onValueChange={onSemanticTypeChange}>
          <SelectTrigger className="h-5 text-[10px] px-1.5 py-0 border-border bg-background">
            <SelectValue placeholder={t('input.selectType')} />
          </SelectTrigger>
          <SelectContent>
            {semanticTypeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Fullscreen & persist toggle */}
      <div className="flex items-center gap-1 ml-auto shrink-0">
        {onImportFile && (
          <button
            onClick={onImportFile}
            className="h-[18px] w-[18px] flex items-center justify-center text-muted-foreground hover:text-foreground rounded border border-border bg-transparent hover:bg-white/[0.03] transition-all duration-150"
            title={t('input.importFile')}
          >
            <FileUp className="h-2.5 w-2.5" />
          </button>
        )}
        {onTogglePersistInput && (
          <button
            onClick={onTogglePersistInput}
            className={cn(
              'h-[18px] w-[18px] flex items-center justify-center rounded border border-border bg-transparent hover:bg-white/[0.03] transition-all duration-150',
              persistInput
                ? 'text-emerald-400 hover:text-emerald-300'
                : 'text-muted-foreground hover:text-foreground',
            )}
            title={
              persistInput
                ? t('input.savedOnReload')
                : t('input.notSavedOnReload')
            }
          >
            {persistInput ? (
              <ShieldCheck className="h-2.5 w-2.5" />
            ) : (
              <ShieldOff className="h-2.5 w-2.5" />
            )}
          </button>
        )}
        {visualizerOverlay && onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            className="h-[18px] w-[18px] flex items-center justify-center text-muted-foreground hover:text-foreground rounded border border-border bg-transparent hover:bg-white/[0.03] transition-all duration-150"
            title={isFullscreen ? t('toolbar.exitFullscreen') : t('toolbar.fullscreen')}
          >
            <Maximize2 className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    </div>

    {/* Content */}
    {visualizerOverlay ? (
      visualizerOverlay
    ) : (
      <div className="flex-1 flex flex-col min-h-0">
        {search.isOpen && (
          <div className="shrink-0 border-b border-border">
            <SearchBar
              ref={search.searchInputRef}
              query={search.query}
              onQueryChange={search.setQuery}
              placeholder={t('input.find')}
              resultCount={search.matchCount}
              currentIndex={search.currentIndex}
              onNext={search.goToNext}
              onPrev={search.goToPrev}
            />
          </div>
        )}
        <textarea
          ref={undoable.ref}
          className="flex-1 p-4 bg-background resize-none font-mono text-sm focus:outline-none"
          placeholder={t('input.placeholder')}
          {...undoable.textareaProps}
          onDragOver={onDragOver}
          onDrop={onDrop}
          spellCheck={false}
        />
      </div>
    )}

    {/* Stats footer */}
    {stats && (
      <div className="h-4 flex items-center px-2.5 text-[9px] text-muted-foreground border-t border-border bg-muted/30 gap-1.5 shrink-0">
        <span>{formatBytes(stats.bytes)}</span>
        <span>·</span>
        <span>{stats.chars.toLocaleString()} ch</span>
        <span>·</span>
        <span>{stats.lines.toLocaleString()} ln</span>
      </div>
    )}
  </div>
);
};


